import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Text } from '@cuemath/web-comps';
import { formatTime, downloadTextFile } from '../../../utils';
import {
  TranscriptWrapper,
  TranscriptHeader,
  ExportButton,
  TranscriptContainer,
  TranscriptSegment,
  SegmentHeader,
  SpeakerBadge,
  TimeStamp,
  SegmentText,
  EmptyState,
} from './TranscriptPanel.styles';

function TranscriptPanel({
  transcript,
  currentTime,
  onSeekToTime,
  isLoading,
  isProcessing,
  classroomId,
  demoDate,
}) {
  const containerRef = useRef(null);
  const activeSegmentRef = useRef(null);

  const getCurrentSegment = () => {
    if (!transcript || !transcript.segments || transcript.segments.length === 0)
      return null;

    // First, try to find an exact match
    const exactMatch = transcript.segments.find(
      segment => currentTime >= segment.startTime && currentTime <= segment.endTime,
    );

    if (exactMatch) return exactMatch;

    // If no exact match, find the nearest segment
    let nearestSegment = transcript.segments[0];
    let minDistance = Math.abs(currentTime - nearestSegment.startTime);

    transcript.segments.forEach(segment => {
      // Calculate distance to the start of this segment
      const distanceToStart = Math.abs(currentTime - segment.startTime);
      // Calculate distance to the end of this segment
      const distanceToEnd = Math.abs(currentTime - segment.endTime);
      // Use the minimum of the two distances
      const distance = Math.min(distanceToStart, distanceToEnd);

      if (distance < minDistance) {
        minDistance = distance;
        nearestSegment = segment;
      }
    });

    return nearestSegment;
  };

  const currentSegment = getCurrentSegment();

  // Auto-scroll to active segment
  useEffect(() => {
    if (currentSegment && activeSegmentRef.current && containerRef.current) {
      const container = containerRef.current;
      const activeElement = activeSegmentRef.current;

      const containerRect = container.getBoundingClientRect();
      const elementRect = activeElement.getBoundingClientRect();

      // Check if the active element is visible in the container
      const isVisible =
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom;

      // Only scroll if the element is not fully visible
      if (!isVisible) {
        // Calculate the scroll position to center the active element
        const elementOffsetTop = activeElement.offsetTop;
        const containerHeight = container.clientHeight;
        const elementHeight = activeElement.clientHeight;

        const targetScrollTop =
          elementOffsetTop - containerHeight / 2 + elementHeight / 2;

        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      }
    }
  }, [currentSegment, currentTime]);

  const handleSegmentClick = segment => {
    onSeekToTime(segment.startTime);
  };

  const handleExport = () => {
    const text = transcript.segments
      .map(
        seg =>
          `[${seg.speaker}] [${formatTime(seg.startTime)} - ${formatTime(seg.endTime)}]\n${seg.text}`,
      )
      .join('\n\n');
    const date = demoDate || new Date().toISOString().split('T')[0];
    downloadTextFile(`transcript-${classroomId}-${date}.txt`, text);
  };

  if (!transcript || !transcript.segments || transcript.segments.length === 0) {
    // Don't show anything when loading or processing - the yellow box above handles this
    if (isLoading || isProcessing) {
      return null;
    }

    // Transcripts are produced by Sonus automatically on
    // teacher_classroom_ended. If a class doesn't have one yet, the
    // user just has to wait — there's nothing to trigger from here.
    return (
      <EmptyState>
        <Text variant="body1Regular">No transcript available yet.</Text>
      </EmptyState>
    );
  }

  return (
    <TranscriptWrapper>
      <TranscriptHeader>
        <ExportButton onClick={handleExport} aria-label="Export transcript">&#8595; Export</ExportButton>
      </TranscriptHeader>
      <TranscriptContainer ref={containerRef}>
        {transcript.segments.map((segment, index) => (
        <TranscriptSegment
          key={index}
          ref={currentSegment === segment ? activeSegmentRef : null}
          isActive={currentSegment === segment}
          onClick={() => handleSegmentClick(segment)}
        >
          <SegmentHeader>
            <SpeakerBadge speaker={segment.speaker}>{segment.speaker}</SpeakerBadge>
            <TimeStamp>
              {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
            </TimeStamp>
          </SegmentHeader>
          <SegmentText variant="body2Regular">{segment.text}</SegmentText>
        </TranscriptSegment>
      ))}
      </TranscriptContainer>
    </TranscriptWrapper>
  );
}

TranscriptPanel.propTypes = {
  transcript: PropTypes.shape({
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        speaker: PropTypes.string,
        text: PropTypes.string.isRequired,
        startTime: PropTypes.number.isRequired,
        endTime: PropTypes.number.isRequired,
        confidence: PropTypes.number,
      }),
    ),
  }),
  currentTime: PropTypes.number.isRequired,
  onSeekToTime: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isProcessing: PropTypes.bool,
  classroomId: PropTypes.string,
  demoDate: PropTypes.string,
};

TranscriptPanel.defaultProps = {
  transcript: null,
  isLoading: false,
  isProcessing: false,
  classroomId: '',
  demoDate: '',
};

export default TranscriptPanel;
